/**
 * Coarse venue list category / shelf classification for the `/venues` index.
 *
 * Derivation is deterministic from real `venueType`, `capacity`, `amenities`,
 * `name`, and programming genres — never fabricated defaults.
 *
 * ## The rule (DES-83, WEB-958) — first match wins
 *
 *  1. Type PARK / OUTDOOR_SPACE → `outdoor`
 *  2. Brewery signal (name / amenity / programming genre) → `breweries`
 *  3. Type RESTAURANT / BAR → `restaurants`
 *  4. Type STADIUM / ARENA → `major`
 *  5. Type THEATER / CONCERT_HALL / CLUB / CINEMA → `stages`
 *  6. Type MUSEUM / GALLERY → `arts`
 *  7. Type COMMUNITY_CENTER / PLACE_OF_WORSHIP / LIBRARY → `community`
 *  8. Type SCHOOL / UNIVERSITY → `campus`
 *  9. Type HOTEL / CONFERENCE_CENTER / EVENT_SPACE → `hotels`
 * 10. Type SPORTS_FACILITY → `sports-facilities`
 * 11. Capacity known: ≥ 5,000 → `major` · ≥ 500 → `mid` · < 500 → `intimate`
 * 12. Else → `null` (the "Other venues" shelf)
 *
 * **Type wins over capacity** (DES-83 decision 3a): Knight Theater (THEATER,
 * 1,200) is `stages`, not `mid`. Rules 1 and 3–10 are one table,
 * {@link VENUE_TYPE_SHELF}, which `venueListCategorySql` (db-core) reads too.
 */
import type { VenueType } from './schemas';

/**
 * Every shelf slug the rule can emit, in **API order** — the order the
 * `/venues/facets` `tiers` array reports them in. The page's render order is a
 * presentation decision and lives in the web app (`groupVenuesByTier`).
 *
 * The six WEB-958 type shelves are appended rather than interleaved so the
 * existing `tiers` entries keep their positions. Every slug here is public:
 * accepted by `?category=`, returned in each item's `category`, and never
 * renamed (old links must keep resolving).
 *
 * `sports-facilities`, not `sports`: `sports` is already a `/venues` page chip
 * (`VENUE_PAGE_CATEGORY_SLUGS`, a *programming* predicate), and `?category=`
 * accepts the union of both vocabularies. A shared slug that meant two
 * different things would make `?category=sports` unable to reach this shelf
 * and would put the chip's count in the shelf's `tiers` entry. The union may
 * only overlap where both vocabularies mean the same thing (`outdoor`,
 * `breweries`) — see {@link VENUE_PAGE_CATEGORY_SLUGS}.
 */
export const VENUE_LIST_CATEGORY_SLUGS = [
  'major',
  'mid',
  'intimate',
  'restaurants',
  'outdoor',
  'breweries',
  'stages',
  'arts',
  'community',
  'campus',
  'hotels',
  'sports-facilities',
] as const;

export type VenueListCategorySlug = (typeof VENUE_LIST_CATEGORY_SLUGS)[number];

/** Capacity floor for the major tier when capacity is present on the row. */
export const VENUE_LIST_MAJOR_CAPACITY_MIN = 5_000;

/** Capacity floor for the mid tier when capacity is present on the row. */
export const VENUE_LIST_MID_CAPACITY_MIN = 500;

/**
 * The shelf a stored `venueType` claims on its own — **total over
 * `VenueType`**, so a new enum member is a compile error until someone decides
 * its shelf. That is the gap WEB-975 fell through: COR-128 added four types
 * that silently reached no shelf.
 *
 * `null` means the type takes no shelf: the row reaches one only through
 * capacity (rule 11), else it sits on "Other venues". SHOPPING_CENTER is
 * deliberately `null` (DES-83 decision 1), and `OTHER` carries no signal.
 *
 * The map does not encode precedence by itself: `outdoor` outranks the brewery
 * signal and every other type shelf ranks below it — see
 * {@link isBreweryOutrankingShelf}. Entry order is the SQL `WHEN` order.
 */
export const VENUE_TYPE_SHELF = {
  PARK: 'outdoor',
  OUTDOOR_SPACE: 'outdoor',
  RESTAURANT: 'restaurants',
  BAR: 'restaurants',
  STADIUM: 'major',
  ARENA: 'major',
  THEATER: 'stages',
  CONCERT_HALL: 'stages',
  CLUB: 'stages',
  CINEMA: 'stages',
  MUSEUM: 'arts',
  GALLERY: 'arts',
  COMMUNITY_CENTER: 'community',
  PLACE_OF_WORSHIP: 'community',
  LIBRARY: 'community',
  SCHOOL: 'campus',
  UNIVERSITY: 'campus',
  HOTEL: 'hotels',
  CONFERENCE_CENTER: 'hotels',
  EVENT_SPACE: 'hotels',
  SPORTS_FACILITY: 'sports-facilities',
  SHOPPING_CENTER: null,
  OTHER: null,
} as const satisfies Record<VenueType, VenueListCategorySlug | null>;

/**
 * True for the one type shelf that ranks ABOVE the brewery signal (rule 1):
 * an outdoor taproom still shelves as `outdoor` (API-1702). Every other type
 * shelf ranks below it, so a THEATER named "… Brewing" is `breweries`.
 */
export const isBreweryOutrankingShelf = (slug: VenueListCategorySlug): boolean =>
  slug === 'outdoor';

/**
 * {@link VENUE_TYPE_SHELF} as a `Map`, so a lookup keyed by an arbitrary stored
 * string cannot reach `Object.prototype` (`venueType = 'constructor'`).
 */
const VENUE_TYPE_SHELF_BY_TYPE = new Map<string, VenueListCategorySlug | null>(
  Object.entries(VENUE_TYPE_SHELF),
);

const typeShelfFor = (venueType: string | null): VenueListCategorySlug | null =>
  venueType === null ? null : (VENUE_TYPE_SHELF_BY_TYPE.get(venueType) ?? null);

export interface VenueListCategoryInput {
  readonly venueType: string | null;
  readonly capacity: number | null;
  readonly amenities: readonly string[] | null;
  readonly name: string;
  readonly programmingGenres: readonly string[] | null;
}

export const isKnownVenueListCategory = (slug: string): slug is VenueListCategorySlug =>
  (VENUE_LIST_CATEGORY_SLUGS as readonly string[]).includes(slug);

const hasBrewerySignal = (input: VenueListCategoryInput): boolean => {
  const nameLower = input.name.toLowerCase();
  if (nameLower.includes('brewery') || nameLower.includes('brewing')) {
    return true;
  }

  if (
    input.amenities?.some((amenity) => {
      const normalized = amenity.toLowerCase();
      return normalized.includes('brewery') || normalized.includes('brewing');
    })
  ) {
    return true;
  }

  if (
    input.programmingGenres?.some((genre) => {
      const normalized = genre.toLowerCase();
      return normalized.includes('brewery') || normalized.includes('brewing');
    })
  ) {
    return true;
  }

  return false;
};

/**
 * Rule 11 — the capacity ladder. Reached only by a row whose type takes no
 * shelf (SHOPPING_CENTER, OTHER, untyped), so `mid` and `intimate` hold only
 * rooms "whose kind is not on record" (DES-83 decision 6).
 */
const deriveCapacityTier = (capacity: number | null): VenueListCategorySlug | null => {
  if (capacity === null) {
    return null;
  }

  if (capacity >= VENUE_LIST_MAJOR_CAPACITY_MIN) {
    return 'major';
  }

  if (capacity >= VENUE_LIST_MID_CAPACITY_MIN) {
    return 'mid';
  }

  return 'intimate';
};

/**
 * Maps a venue row to a coarse list category slug, or `null` when the row
 * lacks enough real data to classify without guessing. The full first-match
 * rule is in this file's header.
 */
export const deriveVenueListCategory = (
  input: VenueListCategoryInput,
): VenueListCategorySlug | null => {
  // COR-62 retired `Location.isOutdoor` (no provider publishes it), so
  // `venueType` is the sole outdoor signal. Positive-only: a venue that is not
  // typed OUTDOOR_SPACE / PARK simply is not shelved as outdoor — it is never
  // asserted to be indoors. COR-91 NULL and OTHER are both no-signal here.
  const typeShelf = typeShelfFor(input.venueType);
  if (typeShelf !== null && isBreweryOutrankingShelf(typeShelf)) {
    return typeShelf;
  }

  if (hasBrewerySignal(input)) {
    return 'breweries';
  }

  // Rules 3–10: type wins over capacity (DES-83 decision 3a).
  if (typeShelf !== null) {
    return typeShelf;
  }

  return deriveCapacityTier(input.capacity);
};

// ---------------------------------------------------------------------------
// API-1692: /venues page category facet (a PROGRAMMING predicate)
// ---------------------------------------------------------------------------

/**
 * The 8 marketing chips the `/venues` index renders (API-1692).
 *
 * A **second, deliberately separate vocabulary** from
 * {@link VENUE_LIST_CATEGORY_SLUGS} above. The shelf slugs answer "what kind of
 * place is this, else how big is the room" (derived from `venueType` /
 * `capacity`); these answer "what can I see here" — which is how the chip
 * actually reads ("Live music" = *I can see a show here*). The two overlap on
 * `outdoor` and `breweries`, and must overlap nowhere else: a shared slug that
 * meant different things in each would be ambiguous under `?category=` (which
 * is why the WEB-958 shelf is `sports-facilities`, not `sports`).
 * `outdoor` is the same venue-intrinsic question in both vocabularies.
 * `breweries` is not: the page chip matches any brewery signal even when the
 * shelf CASE has already labelled the row `outdoor` (API-1702). The slug is
 * still unambiguous; the filter and the returned `category` field are allowed
 * to disagree, the same way a music room can sit on the `intimate` shelf.
 *
 * This list lived as a private const inside `apps/web/.../venues/page.tsx`
 * until API-1692. It has to live down here because the query schema in
 * `./venues.ts` must accept exactly these values, and `packages/validation`
 * cannot import from `apps/web` — the dependency runs the other way. The page
 * now imports it, so the two halves cannot drift back into the defect API-1692
 * fixes: an unrecognised slug transformed to `undefined`, which made
 * `?category=music` return the full unfiltered 1,405-venue list with a `200`.
 */
export const VENUE_PAGE_CATEGORY_SLUGS = [
  'music',
  'food-drink',
  'breweries',
  'art',
  'late-night',
  'outdoor',
  'family',
  'sports',
] as const;

export type VenuePageCategorySlug = (typeof VENUE_PAGE_CATEGORY_SLUGS)[number];

export const isKnownVenuePageCategory = (slug: string): slug is VenuePageCategorySlug =>
  (VENUE_PAGE_CATEGORY_SLUGS as readonly string[]).includes(slug);

/**
 * Local wall-clock hour (inclusive) at which the `late-night` facet begins —
 * an event starting at or after 21:00 in the venue city's timezone.
 *
 * "Local" is load-bearing. `Event.date` is `timestamp without time zone`
 * holding UTC, so reading an hour off it requires an explicit UTC -> local
 * conversion; `venuePageCategorySql` carries the conversion and the note on
 * why the one-sided `AT TIME ZONE` spelling is wrong for this column.
 *
 * Duplicated today by `LATE_NIGHT_HOUR_LOCAL` in
 * `services/backend-api/.../neighborhoods/categories.ts`. Unifying them is left
 * to **API-1701**, which rewrites that exact SQL expression to fix its inverted
 * timezone cast — doing it here would collide with that change for no
 * behavioural gain. (`buildWhyReasons.ts`'s 22/4 window is a *different*
 * concept — a ranking signal over a wrapped range, not a browse facet — and
 * stays separate.)
 */
export const VENUE_LATE_NIGHT_START_HOUR_LOCAL = 21;

/**
 * How one chip decides which venues belong to it.
 *
 * A tagged union rather than one map per kind, because the chip kinds are
 * different predicate *shapes*, not different values of one shape: two read the
 * venue's upcoming events (by category, or by start hour), one reads the venue
 * row itself via the exclusive tier CASE, one reads the brewery signal
 * independently of that CASE, and one has no backing data at all.
 */
export type VenuePageCategoryFacet =
  /** Venue has >= 1 upcoming event whose PRIMARY category is in `categorySlugs`. */
  | { readonly kind: 'eventCategories'; readonly categorySlugs: readonly string[] }
  /** Venue has >= 1 upcoming event starting at or after `minHourLocal`, city-local. */
  | { readonly kind: 'startHourLocal'; readonly minHourLocal: number }
  /** Venue-intrinsic — answered by the existing capacity/type tier rule. */
  | { readonly kind: 'venueTier' }
  /**
   * Venue-intrinsic brewery signal (name / amenities / programming genres),
   * independent of the outdoor-first tier CASE (API-1702).
   */
  | { readonly kind: 'brewerySignal' }
  /** Nothing backs this chip yet; it returns an empty list, on purpose. */
  | { readonly kind: 'unbacked'; readonly reason: string };

/**
 * Every `/venues` chip and the predicate behind it — **total, not partial**.
 *
 * The totality is the point, and it is the second half of the lesson API-1692
 * exists to teach. Widening the query schema stops an unknown slug being
 * silently dropped, but on its own that just moves the silence: a 9th chip
 * added to {@link VENUE_PAGE_CATEGORY_SLUGS} would be *accepted* by the schema
 * and then fall through the SQL builder's default branch to a predicate that
 * can never match — a `200` with an empty list and no error anywhere. Same
 * silent-success failure, new location. No test would catch it either: a suite
 * that iterates the slug list computes its expectation from the same fixtures,
 * so an unbacked slug asserts `[] === []` and passes.
 *
 * Because this `Record` is keyed by {@link VenuePageCategorySlug} with no
 * `Partial`, adding a slug without a facet is a **compile error**. Declaring it
 * `unbacked` is then a deliberate, reviewable act carrying a written reason —
 * which is also the signal a consumer needs in order to decide whether to
 * render a chip that cannot return anything (WEB-859).
 *
 * Category slugs are the **root** taxonomy categories. Verified on production:
 * `event_categories` carries links to root categories only — no event is linked
 * to a subcategory (`music-rock`, `food-tastings`, …) — so matching roots is
 * complete here, not a subset.
 */
export const VENUE_PAGE_CATEGORY_FACETS: Record<VenuePageCategorySlug, VenuePageCategoryFacet> = {
  music: { kind: 'eventCategories', categorySlugs: ['music'] },
  art: { kind: 'eventCategories', categorySlugs: ['art'] },
  'food-drink': { kind: 'eventCategories', categorySlugs: ['food', 'drinks'] },
  sports: { kind: 'eventCategories', categorySlugs: ['sports'] },
  'late-night': { kind: 'startHourLocal', minHourLocal: VENUE_LATE_NIGHT_START_HOUR_LOCAL },
  // `outdoor` is still the exclusive shelf CASE. `breweries` is not: a beer
  // garden typed `OUTDOOR_SPACE` must appear under the Breweries chip even
  // though its returned `category` label stays `outdoor` (API-1702).
  outdoor: { kind: 'venueTier' },
  breweries: { kind: 'brewerySignal' },
  family: {
    kind: 'unbacked',
    reason:
      'The `family` root category exists in the taxonomy but no CLT event is tagged to it ' +
      '(verified against the production branch 2026-09-09: zero links), so the chip is ' +
      'honestly empty rather than silently unfiltered.',
  },
};

/** True for the chips whose predicate reads a venue's upcoming events. */
export const isProgrammingFacet = (facet: VenuePageCategoryFacet): boolean =>
  facet.kind === 'eventCategories' || facet.kind === 'startHourLocal';
